import React from 'react';
import AvailabilityCodeIcon from '../../general/AvailabilityCodeIcon';
import { formatTimeOnlyShort, FormattedPeriod } from '../../../utils/time';
import { Popover, Overlay } from '../../ui-kit';
import { IconExclamationTriangle } from '../../ui-kit/icons';
import SkillIconList from '../../general/SkillIconList';
import { dutyTransactionPath, dtNearingCompletion, DutyRequestIcon } from '../../../utils/dutyTransactions';
import TransactionStateLabel from '../exchanges/TransactionStateLabel';
import moment from 'moment';

const DutyTransactionListWrapper = ({ dutyTransactionList }) => {
  dutyTransactionList = dutyTransactionList || [];

  if (dutyTransactionList.length === 0) return null;

  return (
    <>
      <h5>{I18n.t('duty_planner.requests')}</h5>
      {dutyTransactionList.map(dutyTransaction => (
        <React.Fragment key={dutyTransaction.id}>
          <div>
            <a
              id={'duty_transaction_link'}
              href={dutyTransactionPath(dutyTransaction)}
              target="_blank"
              rel="noreferrer"
            >
              <DutyRequestIcon type={dutyTransaction.type} />{' '}
              <FormattedPeriod start={dutyTransaction.start_time} end={dutyTransaction.end_time} showNowLabel={false} />
            </a>{' '}
            <TransactionStateLabel transactionState={dutyTransaction.state} />{' '}
            {dutyTransaction.availabilityCode && <AvailabilityCodeIcon code={dutyTransaction.availabilityCode} />}{' '}
            {dtNearingCompletion(moment(), dutyTransaction) && (
              <IconExclamationTriangle
                className="text-warning"
                title={I18n.t('schedules.exchanges.absence_request.near_completion')}
              />
            )}
            <br />
            <p>{dutyTransaction.comments}</p>
          </div>
          {dutyTransaction == dutyTransactionList.at(-1) ? null : <hr />}
        </React.Fragment>
      ))}
    </>
  );
};

const hasValidationErrors = interval => Object.entries(interval.validationErrors || {}).length > 0;
const isRelevantInterval = interval => interval.availabilityCode || interval.comments || hasValidationErrors(interval);
const renderValidationErrors = interval => Object.values(interval.validationErrors || {}).join(', ');

const WarningsPopoverContent = ({ intervals, dutyTransactions, alwaysDisplayTimes = false }) => {
  // We only receive alwaysDisplayTimes when the
  // IntervalInformationPopup is being called inside
  // the timeline. In that case we want to keep
  // relevantIntervals as [interval]

  const relevantIntervals = alwaysDisplayTimes ? intervals : intervals.filter(interval => isRelevantInterval(interval));
  return (
    <>
      {relevantIntervals.map(i => (
        <div key={i.start_time}>
          {formatTimeOnlyShort(i.start_time)} - {formatTimeOnlyShort(i.end_time)}{' '}
          <SkillIconList skills={[i.availabilityCode]} /> {i.comments} {renderValidationErrors(i)}
        </div>
      ))}
      <DutyTransactionListWrapper dutyTransactionList={dutyTransactions} canContainAvailabilityCode={true} />
    </>
  );
};

// TODO refactor to functional component
class IntervalInformationPopup extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      shouldShowPopover: false
    };
  }

  shouldDisplayWarningsFor = () => {
    const { intervals, dutyTransactions } = this.props;
    return intervals.some(i => isRelevantInterval(i)) || dutyTransactions.length > 0;
  };

  getWarningIconClass = () => {
    // If there is a duty transaction, the icon should be red
    // If there is not, the icon defaults to light grey (this will be displayed
    // if there is an availability code)
    if (this.props.dutyTransactions.length > 0) return 'text-danger';
    return '';
  };

  showOverlay = e => {
    // target sets the position for the popover
    this.setState({ target: this.state.target || e.target, shouldShowPopover: true });
  };

  hideOverlay = () => {
    this.setState({ shouldShowPopover: false });
  };

  popoverHoverableContent = hasChildren => {
    return (
      <>
        {hasChildren ? (
          <div onMouseEnter={this.showOverlay} onMouseLeave={this.hideOverlay}>
            {this.props.children}
          </div>
        ) : (
          <IconExclamationTriangle
            onMouseEnter={this.showOverlay}
            onMouseLeave={this.hideOverlay}
            className={this.getWarningIconClass()}
          />
        )}
      </>
    );
  };

  render = () => {
    const { intervals, dutyTransactions } = this.props;
    // When we are rendering this in the timeline,
    // we pass the whole item as a child to this component, in order to know
    // if this component has any children, they should be rendered, otherwise
    // (inside Candidate container) just render the icon that was}
    // appearing before.
    const hasChildren = React.Children.count(this.props.children) > 0;
    if (!hasChildren && !this.shouldDisplayWarningsFor()) return null;
    return (
      <>
        {this.popoverHoverableContent(hasChildren)}
        <Overlay
          show={this.state.shouldShowPopover}
          target={this.state.target}
          placement={this.props.overlayPlacement}
          container={this.refs.target}
        >
          <Popover id="popover-positioned-right" onMouseEnter={this.showOverlay} onMouseLeave={this.hideOverlay}>
            <Popover.Body>
              <WarningsPopoverContent
                alwaysDisplayTimes={this.props.alwaysDisplayTimes}
                intervals={intervals}
                dutyTransactions={dutyTransactions}
              />
            </Popover.Body>
          </Popover>
        </Overlay>
      </>
    );
  };
}

export default IntervalInformationPopup;
